/**
 * API Client with HTTP method abstractions
 * Provides a centralized and type-safe way to make HTTP requests
 */

import axios, {
  AxiosInstance,
  AxiosRequestConfig,
  AxiosError,
  AxiosResponse,
} from "axios";
import axiosRetry from "axios-retry";
import { API_CONFIG } from "@/utils/constants";
import { getSessionToken, clearSessionToken } from "@/utils/storage";
import { ApiError, ApiRequestConfig } from "@/types/api.types";
import { logger } from "@/utils/logger";
/**
 * Create and configure Axios instance
 */
const createAxiosInstance = (): AxiosInstance => {
  const instance = axios.create({
    baseURL: API_CONFIG.BASE_URL,
    timeout: API_CONFIG.TIMEOUT,
    withCredentials: true, // ✅ Para Django backend (cookies de sessão)
    headers: {
      "Content-Type": "application/json",
    },
  });

  // Configure automatic retry for failed requests
  axiosRetry(instance, {
    retries: API_CONFIG.RETRY_ATTEMPTS,
    retryDelay: axiosRetry.exponentialDelay,
    retryCondition: (error: AxiosError) => {
      // Retry on network errors or 5xx server errors
      return (
        axiosRetry.isNetworkOrIdempotentRequestError(error) ||
        (error.response?.status ? error.response.status >= 500 : false)
      );
    },
  });

  return instance;
};

const axiosInstance = createAxiosInstance();

/**
 * Request Interceptor - Add authentication token
 */
axiosInstance.interceptors.request.use(
  (config) => {
    const token = getSessionToken();

    // Add session token if exists and not explicitly skipped
    if (token && config.headers && !config.headers["skipAuth"]) {
      config.headers["X-Session-Token"] = token;
    }

    // Remove skipAuth flag before sending
    if (config.headers) {
      delete config.headers["skipAuth"];
    }

    // Log full URL for debugging
    const fullUrl = config.baseURL
      ? `${config.baseURL.replace(/\/$/, "")}/${config.url?.replace(/^\//, "")}`
      : config.url;
    logger.log(`🚀 [API] Request: ${config.method?.toUpperCase()} ${fullUrl}`);

    return config;
  },
  (error) => {
    logger.error("Request interceptor error:", error);
    return Promise.reject(error);
  }
);

/**
 * Response Interceptor - Handle common errors
 */
axiosInstance.interceptors.response.use(
  (response) => response,
  (error: AxiosError) => {
    try {
      logger.error("[Interceptor] Raw error:", error);
      if (error && error.response) {
        logger.error("[Interceptor] Error response:", error.response);
        logger.error(
          "[Interceptor] Error response data:",
          error.response?.data
        );
        logger.error(
          "[Interceptor] Error response status:",
          error.response?.status
        );
      }
    } catch (e) {
      logger.error("Error logging API error:", e);
    }

    const apiError: ApiError = {
      message: "An unexpected error occurred",
      status: error.response?.status,
      response: error.response, // ✅ Mantém a referência ao response original
    };

    if (error.response) {
      // Server responded with error status
      const data = error.response.data as any;
      apiError.message = data?.message || data?.detail || error.message;
      apiError.code = data?.code;
      apiError.errors = data?.errors || data?.detail;

      // Handle authentication errors
      if (error.response.status === 401) {
        clearSessionToken();
        // Redirect to login page
        if (window.location.pathname !== "/login") {
          window.location.href = "/login";
        }
      }

      // Handle forbidden errors
      if (error.response.status === 403) {
        apiError.message = "You do not have permission to perform this action";
      }

      // Handle not found errors
      if (error.response.status === 404) {
        apiError.message = "The requested resource was not found";
      }

      // Handle validation errors (422)
      if (error.response.status === 422) {
        apiError.message = "Validation error. Please check your input.";
        // detail pode ser um array de erros de validação
        if (Array.isArray(data?.detail)) {
          apiError.errors = data.detail;
        }
      }

      // Handle server errors
      if (error.response.status >= 500) {
        apiError.message = "Server error. Please try again later.";
        logger.error("Server error:", error);
      }
    } else if (error.request) {
      // Request made but no response received
      apiError.message =
        "No response from server. Please check your connection.";
      logger.error("Network error:", error);
    } else {
      // Something else happened
      apiError.message = error.message;
      logger.error("Request setup error:", error);
    }

    return Promise.reject(apiError);
  }
);

/**
 * Merge custom config with default config
 */
const mergeConfig = (
  url: string,
  customConfig?: ApiRequestConfig
): AxiosRequestConfig => {
  const config: AxiosRequestConfig = {
    url,
    params: customConfig?.params,
    headers: customConfig?.headers || {},
    timeout: customConfig?.timeout,
  };

  if (customConfig?.skipAuth) {
    config.headers = {
      ...config.headers,
      skipAuth: "true",
    };
  }

  return config;
};

/**
 * API Client Class with HTTP method abstractions
 */
class ApiClient {
  /**
   * GET request
   * @param url - Endpoint URL
   * @param config - Optional request configuration
   * @returns Promise with response data
   */
  async get<T = any>(url: string, config?: ApiRequestConfig): Promise<T> {
    const response: AxiosResponse<T> = await axiosInstance.get(
      url,
      mergeConfig(url, config)
    );
    return response.data;
  }

  /**
   * POST request
   * @param url - Endpoint URL
   * @param data - Request payload
   * @param config - Optional request configuration
   * @returns Promise with response data
   */
  async post<T = any, D = any>(
    url: string,
    data?: D,
    config?: ApiRequestConfig
  ): Promise<T> {
    try {
      logger.log("[ApiClient.post] Request:", { url, data, config });
      const response: AxiosResponse<T> = await axiosInstance.post(
        url,
        data,
        mergeConfig(url, config)
      );
      logger.log("[ApiClient.post] Response:", {
        url,
        status: response.status,
        data: response.data,
      });
      return response.data;
    } catch (error: any) {
      logger.error("[ApiClient.post] Error:", { url, error });
      logger.error("[ApiClient.post] Error response:", error.response?.data);
      logger.error("[ApiClient.post] Error status:", error.response?.status);
      throw error;
    }
  }

  /**
   * PUT request
   * @param url - Endpoint URL
   * @param data - Request payload
   * @param config - Optional request configuration
   * @returns Promise with response data
   */
  async put<T = any, D = any>(
    url: string,
    data?: D,
    config?: ApiRequestConfig
  ): Promise<T> {
    try {
      logger.log("[ApiClient.put] Request:", { url, data, config });
      const response: AxiosResponse<T> = await axiosInstance.put(
        url,
        data,
        mergeConfig(url, config)
      );
      logger.log("[ApiClient.put] Response:", {
        url,
        status: response.status,
        data: response.data,
      });
      return response.data;
    } catch (error: any) {
      logger.error("[ApiClient.put] Error:", { url, error: error.message });
      logger.error("[ApiClient.put] Error response:", error.response);
      logger.error(
        "[ApiClient.put] Error response data:",
        error.response?.data
      );
      logger.error("[ApiClient.put] Error status:", error.response?.status);
      logger.error("[ApiClient.put] Error headers:", error.response?.headers);
      logger.error("[ApiClient.put] Request data:", data);
      logger.error(
        "[ApiClient.put] Full error object:",
        JSON.stringify(error, Object.getOwnPropertyNames(error))
      );
      throw error;
    }
  }

  /**
   * PATCH request
   * @param url - Endpoint URL
   * @param data - Request payload
   * @param config - Optional request configuration
   * @returns Promise with response data
   */
  async patch<T = any, D = any>(
    url: string,
    data?: D,
    config?: ApiRequestConfig
  ): Promise<T> {
    const response: AxiosResponse<T> = await axiosInstance.patch(
      url,
      data,
      mergeConfig(url, config)
    );
    return response.data;
  }

  /**
   * DELETE request
   * @param url - Endpoint URL
   * @param config - Optional request configuration
   * @returns Promise with response data
   */
  async delete<T = any>(url: string, config?: ApiRequestConfig): Promise<T> {
    const response: AxiosResponse<T> = await axiosInstance.delete(
      url,
      mergeConfig(url, config)
    );
    return response.data;
  }

  /**
   * Upload file with multipart/form-data
   * @param url - Endpoint URL
   * @param formData - FormData object
   * @param onUploadProgress - Optional progress callback
   * @returns Promise with response data
   */
  async upload<T = any>(
    url: string,
    formData: FormData,
    onUploadProgress?: (progressEvent: any) => void
  ): Promise<T> {
    const response: AxiosResponse<T> = await axiosInstance.post(url, formData, {
      headers: {
        "Content-Type": "multipart/form-data",
      },
      onUploadProgress,
    });
    return response.data;
  }

  /**
   * Download file
   * @param url - Endpoint URL
   * @param filename - Optional filename for downloaded file
   * @returns Promise with blob
   */
  async download(url: string, filename?: string): Promise<Blob> {
    const response: AxiosResponse<Blob> = await axiosInstance.get(url, {
      responseType: "blob",
    });

    // Trigger download
    if (filename) {
      const downloadUrl = window.URL.createObjectURL(response.data);
      const link = document.createElement("a");
      link.href = downloadUrl;
      link.download = filename;
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(downloadUrl);
    }

    return response.data;
  }

  /**
   * Make a custom request with full control
   * @param config - Axios request configuration
   * @returns Promise with response data
   */
  async request<T = any>(config: AxiosRequestConfig): Promise<T> {
    const response: AxiosResponse<T> = await axiosInstance.request(config);
    return response.data;
  }
}

// Export singleton instance
const apiClient = new ApiClient();
export default apiClient;

// Also export the axios instance for advanced use cases
export { axiosInstance };
